import { useMemo, useCallback } from 'react';

import moment from 'moment';
import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import findIndex from 'lodash/findIndex';

import DataTable from 'src/components/DataTable';
import CheckButton from 'src/components/CheckButton';
import { forceString, timeFormat } from 'src/utils/datetime';

import messages from './messages';

interface ShiftsTableProps {
  error: boolean;
  helperText: string;
  checked: Array<string>;
  onCheck: (checked: Array<string>) => void;
  shifts: UCM.TargetShiftOptionType['shifts'] | undefined;
}

export default function ShiftsTable({
  error,
  shifts,
  checked,
  onCheck,
  helperText,
}: ShiftsTableProps) {
  const { formatMessage } = useIntl();

  const isCheckedAll = useMemo(() => {
    return shifts?.length !== 0 && checked.length === shifts?.length;
  }, [checked.length, shifts?.length]);

  const checkIfExist = useCallback(
    (uuid: string) => {
      return findIndex(checked, (shiftUuid) => shiftUuid === uuid) !== -1;
    },
    [checked],
  );

  const handleCheck = useCallback(
    (shift: UCM.TargetShiftType) => {
      const updatedChecked = [...checked];
      const foundIndex = findIndex(checked, (uuid) => uuid === shift.uuid);

      if (foundIndex === -1) {
        updatedChecked.push(shift.uuid);
      } else {
        updatedChecked.splice(foundIndex, 1);
      }
      onCheck(updatedChecked);
    },
    [checked],
  );

  const handleCheckAll = useCallback(() => {
    if (!shifts || isEmpty(shifts)) return;

    if (shifts?.length === checked.length) {
      onCheck([]);
      return;
    }
    onCheck(map(shifts, (shift) => shift.uuid));
  }, [shifts, checked.length]);

  const columns: Array<{
    id: string;
    label: string | React.ReactNode;
  }> = useMemo(
    () => [
      {
        id: 'date',
        label: (
          <Stack gap={1} flexDirection="row" alignItems="center">
            <CheckButton
              active={isCheckedAll}
              disabled={isEmpty(shifts)}
              onChange={handleCheckAll}
            />
            <Typography
              variant="inherit"
              fontWeight={(theme) => theme.typography.fontWeightMedium}
            >
              {formatMessage(messages.dateColumnLabel)}
            </Typography>
          </Stack>
        ),
        render: (_: Object, shift: UCM.TargetShiftType) => {
          const isSelected = checkIfExist(shift.uuid);

          return (
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <CheckButton
                active={isSelected}
                onChange={() => handleCheck(shift)}
              />
              <Box>
                <Typography variant="inherit">
                  {forceString(shift.startsAt, moment.HTML5_FMT.DATE)}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
      {
        id: 'shiftTimeframe',
        label: formatMessage(messages.shiftColumnLabel),
        align: 'center',
        render: (_: Object, shift: UCM.TargetShiftType) => (
          <Typography variant="inherit">
            {[
              forceString(shift.startsAt, timeFormat),
              forceString(shift.endsAt, timeFormat),
            ].join(' - ')}
          </Typography>
        ),
      },
      {
        id: 'required',
        label: formatMessage(messages.requiredColumnLabel),
        align: 'center',
        render: (_: Object, shift: UCM.TargetShiftType) => (
          <Typography variant="inherit">{shift.availableSlots}</Typography>
        ),
      },
    ],
    [checkIfExist, handleCheckAll, handleCheck, isCheckedAll],
  );

  return (
    <FormControl fullWidth error={error}>
      <DataTable rows={shifts} columns={columns} enabledFooter={false} />
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
}
